import { useContext, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { cleanFullAddress, PropertyAddress } from '@urbanx/agx-ui-components'
import {
  AgxTab,
  TabOption,
  AgxDivider,
  AgxSaveChangesButton,
  SAVE_BUTTON_STATES,
  AgxSpacer,
} from '@urbanx/agx.ui-components2'
import { MenuTitles } from 'constants/menu'
import { ContentLayout, PageLayout } from 'layout'
import { useSelectedAgency, useSelectedCampaign } from 'hooks'
import { AuthContext } from '@urbanx/agx.auth.client'
import { useStaffGetMarketingPackages } from '@urbanx/agx.marketing.client'
import MarketingPackage from './MarketingPackage'
import Launch from './Launch'
import PhotosAndMedia from './photosAndMedia/PhotosAndMedia'
import { FormPrompt } from 'components/FormPrompt'

const TABS: TabOption[] = [
  { id: 'Launch', label: 'Launch', selected: false },
  { id: 'Marketing', label: 'Marketing', selected: false },
  { id: 'PhotosAndMedia', label: 'Photos & Media', selected: false },
]

const CampaignMarketing = () => {
  const { userAccount } = useContext(AuthContext)
  const { agency } = useSelectedAgency()
  const { campaign } = useSelectedCampaign()
  const { data: marketingPackages, isPending: marketingPackagesIsPending } =
    useStaffGetMarketingPackages(
      campaign?.campaignId || '',
      !!campaign?.campaignId
    )
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState<TabOption>(
    TABS.find((t) => t.selected) ?? TABS[0]
  )
  const [savePhotosAndMedia, setSavePhotosAndMedia] = useState<{
    saveChanges: (() => void) | undefined
    isFormDirty: boolean
    isFormValid: boolean
    isPending: boolean
    showSavedMessage: boolean
  }>({
    saveChanges: undefined,
    isFormDirty: false,
    isFormValid: true,
    isPending: false,
    showSavedMessage: false,
  })

  const [saveLaunchDate, setSaveLaunchDate] = useState<{
    saveChanges: (() => void) | undefined
    isFormDirty: boolean
    isFormValid: boolean
    isPending: boolean
    showSavedMessage: boolean
  }>({
    saveChanges: undefined,
    isFormDirty: false,
    isFormValid: false,
    isPending: false,
    showSavedMessage: false,
  })

  const [forceCheckPopup, setForceCheckPopup] = useState(false)
  const [targetTab, setTargetTab] = useState<TabOption | null>(null)

  const handleTabChange = useCallback(
    (tab: TabOption) => {
      if (selectedTab.id === TABS[2].id && savePhotosAndMedia.isFormDirty) {
        setForceCheckPopup(true)
        setTargetTab(tab)
      } else {
        setSelectedTab(tab)
      }

      if (selectedTab.id === TABS[0].id && saveLaunchDate.isFormDirty) {
        setForceCheckPopup(true)
        setTargetTab(tab)
      } else {
        setSelectedTab(tab)
      }
    },
    [selectedTab.id, savePhotosAndMedia.isFormDirty, saveLaunchDate.isFormDirty]
  )

  const handleSaveStateChange = useCallback(
    (state: {
      saveChanges: (() => void) | undefined
      isFormDirty: boolean
      isFormValid: boolean
      isPending: boolean
      showSavedMessage: boolean
    }) => {
      setSavePhotosAndMedia(state)
    },
    []
  )

  const handleSaveLaunchStateChange = useCallback(
    (state: {
      saveChanges: (() => void) | undefined
      isFormDirty: boolean
      isFormValid: boolean
      isPending: boolean
      showSavedMessage: boolean
    }) => {
      setSaveLaunchDate(state)
    },
    []
  )

  const onConfirmNavigation = useCallback(() => {
    if (targetTab) {
      setSelectedTab(targetTab)
      setTargetTab(null)
    }
    setForceCheckPopup(false)
  }, [targetTab])

  const hasUnsavedChanges =
    selectedTab.id === TABS[2].id && savePhotosAndMedia.isFormDirty

  const savingState = useMemo(() => {
    if (savePhotosAndMedia.showSavedMessage) return SAVE_BUTTON_STATES.SAVED
    if (savePhotosAndMedia.isPending) return SAVE_BUTTON_STATES.SAVING
    return SAVE_BUTTON_STATES.UNSAVED
  }, [savePhotosAndMedia.showSavedMessage, savePhotosAndMedia.isPending])

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <FormPrompt
        hasUnsavedChanges={hasUnsavedChanges}
        forceCheck={forceCheckPopup}
        resetForceCheck={setForceCheckPopup}
        onBack={onConfirmNavigation}
      />

      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CAMPAIGN_MARKETING}
        isCampaigns={true}
      >
        <AgxSpacer direction="vertical" fill>
          <AgxSpacer id="subPageHeadingContainer" direction="vertical" fill>
            <AgxSpacer
              direction="vertical"
              gap="spacing-xl"
              fill
              padding={{ bottom: 'spacing-3xl' }}
            >
              <AgxSpacer
                direction="horizontal"
                contentJustification="space-between"
                contentAlignment="center"
                fill
              >
                <AgxSpacer direction="vertical">
                  <AgxTab
                    options={TABS.map((tab) => ({
                      ...tab,
                      selected: selectedTab.id === tab.id,
                    }))}
                    onChange={handleTabChange}
                  />
                </AgxSpacer>
                {selectedTab.id === TABS[2].id && (
                  <AgxSaveChangesButton
                    id="saveCampaignMarketingChanges"
                    text="Save Changes"
                    size="medium"
                    variant="primary"
                    onClick={savePhotosAndMedia.saveChanges}
                    state={savingState}
                    disabled={
                      !savePhotosAndMedia.isFormDirty ||
                      !savePhotosAndMedia.isFormValid ||
                      savePhotosAndMedia.isPending
                    }
                  />
                )}

                {selectedTab.id === TABS[0].id && (
                  <AgxSaveChangesButton
                    id="saveCampaignMarketingChanges"
                    text="Save Changes"
                    size="medium"
                    variant="primary"
                    onClick={saveLaunchDate.saveChanges}
                    state={savingState}
                    disabled={
                      !saveLaunchDate.isFormDirty ||
                      !saveLaunchDate.isFormValid ||
                      saveLaunchDate.isPending
                    }
                  />
                )}
              </AgxSpacer>
            </AgxSpacer>

            <div className="workflowTask-divider-stretch">
              <AgxDivider variant="horizontal" />
            </div>
          </AgxSpacer>
          {selectedTab.id === TABS[0].id && (
            <Launch
              campaign={campaign}
              onSaveStateChange={handleSaveLaunchStateChange}
            />
          )}

          {selectedTab.id === TABS[1].id && (
            <MarketingPackage
              marketingPackages={marketingPackages ?? []}
              marketingPackagesIsPending={marketingPackagesIsPending}
              navigate={navigate}
              agency={agency}
              campaign={campaign}
            />
          )}

          {selectedTab.id === TABS[2].id && (
            <PhotosAndMedia
              campaign={campaign}
              onSaveStateChange={handleSaveStateChange}
            />
          )}
        </AgxSpacer>
      </ContentLayout>
    </PageLayout>
  )
}

export default CampaignMarketing
